import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useT } from '@/lib/i18n/useT';
import { ApiError } from '@/lib/api/errors';
import { reporterApi } from './api';
import type { ReporterSession } from './api';

/**
 * PRD §12 Reporter View — public, magic-link-scoped surface for field staff.
 *
 * Scope of this skeleton (Phase 2):
 *   • Route `/reporter/:token` mounts this component outside the admin shell.
 *   • Token is verified server-side; on 401/410 the user sees a clear "link
 *     expired" state (no retry — they must request a new link from the brand).
 *   • Four tabs (Today / Inventory / Notes / Claims) render placeholder cards
 *     wired to the event scope returned by /reporter/magic-link/verify.
 *   • Reporter Platform Terms are gated with a one-time modal (PRD §13.6).
 *   • Offline queue (IndexedDB + SW) lands in Phase 3 — the placeholder cards
 *     call out that notes will queue locally and sync when the connection
 *     comes back.
 *
 * Why no Zustand / authStore: reporters share kiosks with admins and we never
 * want their scoped token landing in the same persisted slot as a logged-in
 * brand manager. The token stays in component state for the lifetime of the
 * tab — close the tab, re-open the link, re-verify. Cheap and isolated.
 */
const REPORTER_TERMS_VERSION = 'v1';

type TabId = 'today' | 'inventory' | 'notes' | 'claims';

const TABS: TabId[] = ['today', 'inventory', 'notes', 'claims'];

export function ReporterView() {
  const { token } = useParams<{ token: string }>();
  const t = useT();

  const { data, error, isLoading } = useQuery({
    queryKey: ['reporter', 'verify', token],
    queryFn: () => reporterApi.verify(token!),
    enabled: !!token,
    retry: false,
  });

  // Terms acceptance is tracked locally — the backend remembers it too once
  // /reporter/magic-link/accept-terms ships, but for the skeleton we trust
  // the client until the next reload, which forces re-acceptance after the
  // 24h link expires anyway.
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('today');

  useEffect(() => {
    if (data && !data.termsRequired) setTermsAccepted(true);
  }, [data]);

  if (!token) {
    return <CenteredCard heading={t('reporter.invalidLink.title')} body={t('reporter.invalidLink.body')} />;
  }

  if (isLoading) {
    return <CenteredCard heading={t('reporter.verifying.title')} body={t('reporter.verifying.body')} />;
  }

  if (error || !data) {
    const status = error instanceof ApiError ? error.status : undefined;
    const expired = status === 410;
    return (
      <CenteredCard
        heading={
          expired
            ? t('reporter.expired.title')
            : t('reporter.invalidLink.title')
        }
        body={
          expired
            ? t('reporter.expired.body')
            : t('reporter.invalidLink.body')
        }
      />
    );
  }

  if (!termsAccepted) {
    return (
      <ReporterTermsModal
        session={data}
        token={token}
        onAccept={() => setTermsAccepted(true)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-xk-bg-deep)] text-[var(--color-xk-lavender)]">
      <header className="border-b border-[var(--color-xk-border)] px-4 py-3">
        <div className="mx-auto flex max-w-4xl items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest opacity-60">
              {t('reporter.header.eyebrow')}
            </p>
            <h1 className="text-base font-bold">{data.eventName}</h1>
          </div>
          <div className="text-right text-[11px] opacity-60">
            <div>{data.name}</div>
            <div>{t('reporter.header.expiresAt', { date: formatExpiry(data.expiresAt) })}</div>
          </div>
        </div>
      </header>

      <nav className="border-b border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)]">
        <div className="mx-auto flex max-w-4xl gap-1 overflow-x-auto px-2 py-2">
          {TABS.map((id) => {
            const active = activeTab === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                  active
                    ? 'bg-[var(--color-xk-accent)] text-[var(--color-xk-bg-deep)]'
                    : 'text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]'
                }`}
              >
                {t(`reporter.tabs.${id}`)}
              </button>
            );
          })}
        </div>
      </nav>

      <main className="mx-auto max-w-4xl px-4 py-6">
        {activeTab === 'today' && <TodayTab session={data} />}
        {activeTab === 'inventory' && <InventoryTab session={data} />}
        {activeTab === 'notes' && <NotesTab />}
        {activeTab === 'claims' && <ClaimsTab session={data} />}
      </main>
    </div>
  );
}

function CenteredCard({ heading, body }: { heading: string; body: string }) {
  return (
    <div className="grid min-h-screen place-items-center bg-[var(--color-xk-bg-deep)] px-4 text-[var(--color-xk-lavender)]">
      <div className="max-w-sm rounded-2xl border border-[var(--color-xk-border)] p-6 text-center">
        <h1 className="text-lg font-bold">{heading}</h1>
        <p className="mt-2 text-sm opacity-70">{body}</p>
      </div>
    </div>
  );
}

function ReporterTermsModal({
  session,
  token,
  onAccept,
}: {
  session: ReporterSession;
  token: string;
  onAccept: () => void;
}) {
  const t = useT();
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const handleAccept = async () => {
    if (!agreed || submitting) return;
    setSubmitting(true);
    try {
      // Persist acceptance against the real magic-link token so the manager's
      // Planner flips the reporter Pending → Confirmed. Passing an empty token
      // hit the backend's no-op path and left termsAccepted = false forever.
      await reporterApi.acceptTerms(token, REPORTER_TERMS_VERSION).catch(() => {
        // Don't block the reporter if the audit write transiently fails.
      });
    } finally {
      setSubmitting(false);
      onAccept();
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-[var(--color-xk-bg-deep)] px-4 text-[var(--color-xk-lavender)]">
      <div className="max-w-md rounded-2xl border border-[var(--color-xk-border)] p-6">
        <p className="text-[10px] uppercase tracking-widest opacity-60">
          {t('reporter.terms.eyebrow')}
        </p>
        <h1 className="mt-1 text-lg font-bold">
          {t('reporter.terms.title', { event: session.eventName })}
        </h1>
        <p className="mt-3 text-sm opacity-80">{t('reporter.terms.body')}</p>
        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm opacity-80">
          <li>{t('reporter.terms.point1')}</li>
          <li>{t('reporter.terms.point2')}</li>
          <li>{t('reporter.terms.point3')}</li>
        </ul>
        <label className="mt-4 flex cursor-pointer items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-4 w-4"
          />
          <span>{t('reporter.terms.checkbox')}</span>
        </label>
        <button
          type="button"
          disabled={!agreed || submitting}
          onClick={handleAccept}
          className="mt-4 w-full rounded-full bg-[var(--color-xk-accent)] px-4 py-2 text-sm font-bold text-[var(--color-xk-bg-deep)] disabled:opacity-50"
        >
          {submitting ? t('reporter.terms.submitting') : t('reporter.terms.accept')}
        </button>
      </div>
    </div>
  );
}

function TodayTab({ session }: { session: ReporterSession }) {
  const t = useT();
  // PRD §16 — Atledi briefing inside Reporter Today. Reporters hit this
  // surface with a magic-link (no JWT), so we generate a deterministic
  // contextual greeting client-side using the same shape AtlediService
  // would return. A future enhancement adds a public `/atledi/reporter-
  // today/:token` that routes through the magic-link auth.
  const slot = atlediTodaySlot();
  const briefing = t(`reporter.today.atlediBriefing.${slot}`, {
    event: session.eventName,
  });
  return (
    <div className="space-y-4">
      <PlaceholderCard
        title={t('reporter.today.scheduleTitle')}
        body={t('reporter.today.scheduleBody', { event: session.eventName })}
      />
      <div className="rounded-2xl border border-[var(--color-accent-20)] bg-[var(--color-accent-06)] p-4">
        <p className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-xk-accent)]">
          {t('reporter.today.atlediEyebrow')}
        </p>
        <h2 className="mt-1 text-sm font-bold">
          {t('reporter.today.atlediTitle')}
        </h2>
        <p className="mt-2 text-sm leading-relaxed opacity-90">{briefing}</p>
      </div>
    </div>
  );
}

function atlediTodaySlot(): 'morning' | 'afternoon' | 'evening' | 'night' {
  const h = new Date().getHours();
  if (h < 6) return 'night';
  if (h < 12) return 'morning';
  if (h < 18) return 'afternoon';
  return 'evening';
}

function InventoryTab({ session }: { session: ReporterSession }) {
  const t = useT();
  return (
    <div className="space-y-4">
      <PlaceholderCard
        title={t('reporter.inventory.title')}
        body={t('reporter.inventory.body', { event: session.eventName })}
      />
    </div>
  );
}

function NotesTab() {
  const t = useT();
  return (
    <div className="space-y-4">
      <PlaceholderCard
        title={t('reporter.notes.title')}
        body={t('reporter.notes.body')}
      />
      <PlaceholderCard
        title={t('reporter.notes.offlineTitle')}
        body={t('reporter.notes.offlineBody')}
      />
    </div>
  );
}

function ClaimsTab({ session }: { session: ReporterSession }) {
  const t = useT();
  return (
    <div className="space-y-4">
      <PlaceholderCard
        title={t('reporter.claims.title')}
        body={t('reporter.claims.body', { event: session.eventName })}
      />
    </div>
  );
}

function PlaceholderCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-2xl border border-[var(--color-xk-border)] p-4">
      <h2 className="text-sm font-bold">{title}</h2>
      <p className="mt-2 text-sm opacity-70">{body}</p>
    </div>
  );
}

function formatExpiry(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
